import { useEffect, useState } from "react";
import { ILead, IMultipleEvent } from "../../../../../redux/leads/leads.types";
import { Dialog } from "../../../../../components/Dialogs/Dialog";
import { Button, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { EventTimeAutoSearch } from "../../../../../components/AutoCompleteSearches/EventTimeAutoSearch";
import { CustomDatePicker } from "../../../../../components/formsComponents/CustomDatePicker";
import { VenueAutoSearch } from "../../../../../components/AutoCompleteSearches/VenueAutoSearch";
import { useTimezoneFormatter } from "../../../../../hooks/useTimezoneFormatter";
import { EventAutoSearch } from "../../../../../components/AutoCompleteSearches/EventAutoSearch";
import { MuiNumberField } from "../../../../../components/formsComponents/MuiNumberField";
import { isDeleted } from "yjs";
import { useFormik } from "formik";
import { useFormikAutomation } from "../../../../../hooks/useFormikAutomation";
import { api } from "../../../../../api/api";
import { buildLeadsQueryParams, IBuildLeadsQueryParams } from "./DuplicateCancelationReasonDialog.helpers";
import { LoadState } from "../../../../../constants/enums";




export interface IDuplicateReferenceSavePayload {
    leads_uuid: string | null;
    contact_number_one: string;
}

interface IDuplicateCancelationReasonDialogProps {
    open: boolean;
    onSelect: (data: IDuplicateReferenceSavePayload) => void;
    onClose: () => void;
}

export const DuplicateCancelationReasonDialog = ({
    open,
    onSelect,
    onClose,
}: IDuplicateCancelationReasonDialogProps) => {
    const [leadsState, setLeadsState] = useState<{
        data: ILead[];
        loading: LoadState;
        error: string | null;
    }>({
        data: [],
        loading: LoadState.NotLoaded,
        error: null,
    })

    const { values, errors, isValid, setValues, handleChange, handleSubmit, setSubmitting } =
        useFormik<IBuildLeadsQueryParams>({
            initialValues: {
                contact_number_one: "",
                name_of_guest: "",
                preferred_venue: "",
                preferred_venue_uuid: "",
                function_date: "",
                event_time: "",
                event_time_uuid: "",
                event_type: "",
            },
            validateOnChange: true,
            validate: (values) => {
                const errors: any = {};
                if (!values.contact_number_one) {
                    errors.contact_number_one = "Phone number is required";
                }
                else if (!/^\d{10}$/.test(values.contact_number_one)) {
                    errors.contact_number_one =
                        "Phone number must be 10 digits";
                }

                return errors;
            },
            onSubmit: async (values, { setSubmitting }) => {
                setSubmitting(true);
                onSelect({
                    leads_uuid: null,
                    contact_number_one: values.contact_number_one,
                });
                setSubmitting(false);
            },
        });


    const fetchResults = async () => {
        setLeadsState((prev) => ({ ...prev, loading: LoadState.InProgress }));
        try {
            const response = await api.get("/leads/get-leads", {
                params: {
                    advanceFilter: buildLeadsQueryParams(values)
                },
            });
            setLeadsState({
                data: response.data.data as ILead[],
                loading: LoadState.Loaded,
                error: null,
            });
        } catch (err) {
            console.error("Error fetching leads:", err);
            setLeadsState({
                data: [],
                loading: LoadState.Failed,
                error: "Failed to fetch leads",
            });
        }
    }


    useEffect(() => {
        if (values.contact_number_one.length > 3)
            fetchResults();
    }, [values])


    const isValidSearch = isValid;

    return (
        <>
            <Dialog
                open={open}
                title={"Duplicate Reference"}
                onClose={onClose}
                size="lg"
                contentWrappedWithForm={{ onSubmit: handleSubmit }}
                actions={[
                    {
                        type: "submit" as const,
                        label: "Save",
                        variant: "contained" as const,
                        disabled: !isValid,
                    },
                ]}
            >

                {/* Search Area */}

                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <MuiNumberField
                            name="contact_number_one"
                            label="Phone Number"
                            value={values.contact_number_one || ""}
                            onChange={handleChange}
                            error={errors.contact_number_one}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <VenueAutoSearch
                            label="Preferred Venue"
                            venue_uuid={values.preferred_venue_uuid || undefined}
                            venue_name={values.preferred_venue || undefined}
                            disabled={!isValidSearch}
                            value={{
                                venue_uuid: values.preferred_venue_uuid,
                                venue_name: values.preferred_venue,
                            }}
                            onSelect={(newValue: any) => {
                                setValues((prev) => ({
                                    ...prev,
                                    preferred_venue_uuid: newValue.venue_uuid ?? "",
                                    preferred_venue: newValue.venue_name ?? "",
                                }));
                            }}
                            error={errors.preferred_venue_uuid}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <CustomDatePicker
                            placeholder="Event Date"
                            fullWidth
                            value={values.function_date || null}
                            disabled={!isValidSearch}
                            onChange={(newValue) => {
                                let extractedMonth = "";
                                if (newValue) {
                                    const date = new Date(newValue);
                                    extractedMonth = date.toLocaleString("default", {
                                        month: "long",
                                    });
                                }
                                setValues((prev) => ({
                                    ...prev,
                                    function_date: newValue,
                                    function_month: extractedMonth,
                                }));
                            }}
                            error={!!errors.function_date}
                        />
                    </Grid>


                    {/* Event Time */}
                    <Grid item xs={12} md={3}>
                        <EventTimeAutoSearch
                            label="Event Time"
                            fullWidth
                            event_timing_uuid={values.event_time_uuid}
                            event_name={values.event_time}
                            disabled={!isValidSearch}
                            value={{
                                event_timing_uuid: values.event_time_uuid,
                                event_name: values.event_time,
                            }}
                            onSelect={(newValue: any) => {
                                setValues((prev) => ({
                                    ...prev,
                                    event_time_uuid: newValue.event_timing_uuid ?? "",
                                    event_time: newValue.event_name ?? "",
                                }));
                            }}
                            error={errors.preferred_venue_uuid}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <EventAutoSearch
                            label="Event Type"
                            value={values.event_type}
                            onSelect={(event_type: any, event_uuid: any) => {
                                setValues((prev) => ({
                                    ...prev,
                                    event_type,
                                    event_uuid,
                                }));
                            }}
                            multiple={false}
                            disabled={!isValidSearch}
                        />
                    </Grid>
                </Grid>

                {leadsState.loading === LoadState.InProgress ? (
                    <div>Loading...</div>
                ) : leadsState.error ? (
                    <div>{leadsState.error}</div>
                ) : leadsState.loading === LoadState.Loaded && leadsState.data.length === 0 ? (
                    <div>No leads found.</div>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Actions</TableCell>
                                    <TableCell>Guest Name</TableCell>
                                    <TableCell>Contact No</TableCell>
                                    <TableCell>Event Date</TableCell>
                                    <TableCell>Preferred Venue</TableCell>
                                    <TableCell>Event Time</TableCell>
                                    <TableCell>Event Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {leadsState.data.map((lead) => (
                                    <TableRow key={lead.leads_uuid}>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() =>
                                                    onSelect({
                                                        leads_uuid: lead.leads_uuid,
                                                        contact_number_one:
                                                            lead.contact_number_one ?? "",
                                                    })
                                                }
                                            >
                                                Select
                                            </Button>
                                        </TableCell>
                                        <TableCell>{lead.name_of_guest}</TableCell>
                                        <TableCell>{lead.contact_number_one}</TableCell>
                                        <TableCell>{lead.function_date}</TableCell>
                                        <TableCell>{lead.preferred_venue}</TableCell>
                                        <TableCell>{lead.event_time}</TableCell>
                                        <TableCell>{lead.event_type}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

            </Dialog>
        </>
    );
};



